/**
 * ChatWidget — floating FAQ chat bubble for extension detail pages.
 *
 * P39 F3 (WI#843): Provides instant FAQ answers via Claude Haiku.
 * When the user's question sounds like a bug or feature request,
 * shows a prompt to open the refinement form (F4).
 */
import React, { useState, useRef, useEffect } from 'react';
import { colors, spacing, typography, radius } from '../theme';
const WIDGET_WIDTH = 340;
const WIDGET_HEIGHT = 440;
export const ChatWidget = ({ extensionId, onOpenFeedback }) => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: 'Hi! I can answer questions about this extension. What would you like to know?',
        },
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [suggestFeedback, setSuggestFeedback] = useState(false);
    const bottomRef = useRef(null);
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, open]);
    const send = async () => {
        const question = input.trim();
        if (!question || loading)
            return;
        const userMessage = { role: 'user', content: question };
        const nextMessages = [...messages, userMessage];
        setMessages(nextMessages);
        setInput('');
        setLoading(true);
        setSuggestFeedback(false);
        try {
            // Only send last 6 messages as history to keep prompt small
            const history = nextMessages.slice(-7, -1);
            const res = await fetch('/api/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ extensionId, question, history }),
            });
            if (res.ok) {
                const data = (await res.json());
                setMessages((prev) => [...prev, { role: 'assistant', content: data.answer }]);
                setSuggestFeedback(data.suggestFeedback);
            }
            else {
                setMessages((prev) => [
                    ...prev,
                    { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' },
                ]);
            }
        }
        catch {
            setMessages((prev) => [
                ...prev,
                { role: 'assistant', content: 'Network error. Please try again or email support@cool-tools.app.' },
            ]);
        }
        finally {
            setLoading(false);
        }
    };
    const handleKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void send();
        }
    };
    return (React.createElement(React.Fragment, null,
        open && (React.createElement("div", { style: {
                position: 'fixed',
                bottom: 80,
                right: 20,
                width: WIDGET_WIDTH,
                height: WIDGET_HEIGHT,
                background: colors.white,
                borderRadius: radius.card,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1000,
                overflow: 'hidden',
                border: `1px solid ${colors.border}`,
            } },
            React.createElement("div", { style: {
                    padding: `${spacing.sm}px ${spacing.md}px`,
                    background: colors.primary,
                    color: colors.white,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                } },
                React.createElement("div", null,
                    React.createElement("div", { style: { fontFamily: typography.fontFamily, fontWeight: 700, fontSize: typography.fontSizeSm } }, "Support Chat"),
                    React.createElement("div", { style: { fontFamily: typography.fontFamily, fontSize: typography.fontSizeXs, opacity: 0.85 } }, "Powered by Claude")),
                React.createElement("button", { onClick: () => setOpen(false), style: {
                        background: 'none',
                        border: 'none',
                        color: colors.white,
                        cursor: 'pointer',
                        fontSize: 18,
                        lineHeight: 1,
                        padding: 4,
                    }, "aria-label": "Close chat" }, "\u00D7")),
            React.createElement("div", { style: {
                    flex: 1,
                    overflowY: 'auto',
                    padding: spacing.sm,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing.xs,
                } },
                messages.map((msg, i) => (React.createElement("div", { key: i, style: {
                        display: 'flex',
                        justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    } },
                    React.createElement("div", { style: {
                            maxWidth: '82%',
                            padding: `${spacing.xs}px ${spacing.sm}px`,
                            borderRadius: 12,
                            background: msg.role === 'user' ? colors.primary : colors.surface,
                            color: msg.role === 'user' ? colors.white : colors.textPrimary,
                            fontFamily: typography.fontFamily,
                            fontSize: typography.fontSizeSm,
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                        } }, msg.content)))),
                loading && (React.createElement("div", { style: { display: 'flex', justifyContent: 'flex-start' } },
                    React.createElement("div", { style: {
                            padding: `${spacing.xs}px ${spacing.sm}px`,
                            borderRadius: 12,
                            background: colors.surface,
                            color: colors.textSecondary,
                            fontFamily: typography.fontFamily,
                            fontSize: typography.fontSizeSm,
                        } }, "..."))),
                suggestFeedback && onOpenFeedback && (React.createElement("div", { style: {
                        padding: `${spacing.xs}px ${spacing.sm}px`,
                        borderRadius: 12,
                        background: colors.primary + '12',
                        border: `1px solid ${colors.primary}40`,
                        fontFamily: typography.fontFamily,
                        fontSize: typography.fontSizeXs,
                        color: colors.textPrimary,
                    } },
                    React.createElement("strong", null, "Sounds like you found a bug or have a feature idea?"),
                    React.createElement("br", null),
                    React.createElement("button", { onClick: onOpenFeedback, style: {
                            marginTop: 4,
                            background: 'none',
                            border: 'none',
                            color: colors.primary,
                            cursor: 'pointer',
                            fontFamily: typography.fontFamily,
                            fontSize: typography.fontSizeXs,
                            fontWeight: 600,
                            padding: 0,
                            textDecoration: 'underline',
                        } }, "Submit Bug / Feature Request \u2192"))),
                React.createElement("div", { ref: bottomRef })),
            React.createElement("div", { style: {
                    padding: spacing.sm,
                    borderTop: `1px solid ${colors.border}`,
                    display: 'flex',
                    gap: spacing.xs,
                } },
                React.createElement("input", { value: input, onChange: (e) => setInput(e.target.value), onKeyDown: handleKey, placeholder: "Ask a question\u2026", disabled: loading, style: {
                        flex: 1,
                        padding: `${spacing.xs}px ${spacing.sm}px`,
                        border: `1px solid ${colors.border}`,
                        borderRadius: 20,
                        fontFamily: typography.fontFamily,
                        fontSize: typography.fontSizeSm,
                        outline: 'none',
                        background: colors.white,
                    } }),
                React.createElement("button", { onClick: () => void send(), disabled: loading || !input.trim(), style: {
                        padding: `${spacing.xs}px ${spacing.sm}px`,
                        background: colors.primary,
                        color: colors.white,
                        border: 'none',
                        borderRadius: 20,
                        cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                        opacity: loading || !input.trim() ? 0.5 : 1,
                        fontFamily: typography.fontFamily,
                        fontSize: typography.fontSizeSm,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                    } }, "Send")))),
        React.createElement("button", { onClick: () => setOpen((v) => !v), "aria-label": open ? 'Close support chat' : 'Open support chat', style: {
                position: 'fixed',
                bottom: 20,
                right: 20,
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: colors.primary,
                color: colors.white,
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                zIndex: 1001,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                transition: 'transform 0.2s ease',
            } }, open ? '×' : '💬')));
};
